import { FormEvent, useCallback, useEffect, useId, useState } from 'react';
import { useI18n } from '../../i18n';
import type { Strings } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { errorText, rpc, type AppError } from '../admin/api';
import { StaffFrame } from '../r3/common';

// Phase 4 of the topic & follow-up check-in revision (spec §12.B, migrations
// 0024/0025/0028): a school admin's own custom follow-up questions, one of
// the six SWIPE research areas each, answered on a 3-option scale template.
// Always Act-lens with no axis (spec §14.3/§14.4) — those two fields are
// platform-authored only and never appear in this form. Draft -> pending ->
// approved -> retired; a head of school approves (HeadR5Pages.tsx).

type Area = { code: string; sort_order: number };
type ScaleOption = { sort_order: number; emoji: string; label: string };
type ScaleTemplate = { code: string; options: ScaleOption[] };
type Question = {
  id: string;
  category_code: string;
  question_text: string;
  severity_weight: 0 | 1 | 2;
  scale_template_code: string;
  direction: 'ascending' | 'descending';
  status: 'draft' | 'pending' | 'approved' | 'retired';
  created_at: string;
  approved_at: string | null;
};

const SEVERITY = [0, 1, 2] as const;

export function QuestionsPage() {
  const { t } = useI18n();
  return (
    <StaffFrame roles={['school_admin']} title={t['r5.sq.title']} extraNav={[{ icon: '📋', label: t['r5.sq.nav'], href: '/questions', active: true }]}>
      {(ctx) => <Body schoolId={ctx.active_school_id ?? ''} />}
    </StaffFrame>
  );
}

const emptyForm = { id: null as string | null, category: '', scale: '', direction: 'ascending' as 'ascending' | 'descending', text: '', weight: 0 as 0 | 1 | 2 };

function Body({ schoolId }: { schoolId: string }) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [areas, setAreas] = useState<Area[] | null>(null);
  const [scales, setScales] = useState<ScaleTemplate[] | null>(null);
  const [questions, setQuestions] = useState<Question[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ kind: 'delete' | 'retire'; q: Question } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmErr, setConfirmErr] = useState<AppError | null>(null);

  const load = useCallback(async () => {
    const [a, s, q] = await Promise.all([
      rpc<Area[]>('active_categories'),
      rpc<ScaleTemplate[]>('active_scale_templates'),
      rpc<Question[]>('school_admin_list_questions', { p_school_id: schoolId }),
    ]);
    if (a.error) setErr(a.error);
    else setAreas(a.data);
    if (!s.error) setScales(s.data);
    if (q.error) {
      if (!a.error) setErr(q.error);
    } else setQuestions(q.data);
  }, [schoolId]);
  useEffect(() => {
    load();
  }, [load]);

  // Default the pickers to the first option once the lists arrive.
  useEffect(() => {
    setForm((f) => ({
      ...f,
      category: f.category || areas?.[0]?.code || '',
      scale: f.scale || scales?.[0]?.code || '',
    }));
  }, [areas, scales]);

  function startEdit(q: Question) {
    setForm({ id: q.id, category: q.category_code, scale: q.scale_template_code, direction: q.direction, text: q.question_text, weight: q.severity_weight });
    setErr(null);
  }
  function cancelEdit() {
    setForm((f) => ({ ...emptyForm, category: f.category, scale: f.scale }));
    setErr(null);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const r = form.id
      ? await rpc('school_admin_update_question', {
          p_question_id: form.id,
          p_scale_template_code: form.scale,
          p_question_text: form.text,
          p_direction: form.direction,
          p_severity_weight: form.weight,
        })
      : await rpc('school_admin_create_question', {
          p_school_id: schoolId,
          p_category_code: form.category,
          p_scale_template_code: form.scale,
          p_question_text: form.text,
          p_direction: form.direction,
          p_severity_weight: form.weight,
        });
    setBusy(false);
    if (r.error) return setErr(r.error);
    announce(t['r5.sq.saved']);
    cancelEdit();
    load();
  }

  async function submit(q: Question) {
    const r = await rpc('school_admin_submit_question', { p_question_id: q.id });
    if (r.error) setErr(r.error);
    else {
      announce(t['r5.sq.submitted']);
      load();
    }
  }

  async function runConfirm() {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmErr(null);
    const r =
      confirm.kind === 'delete'
        ? await rpc('school_admin_delete_question', { p_question_id: confirm.q.id })
        : await rpc('school_admin_retire_question', { p_question_id: confirm.q.id });
    setConfirmBusy(false);
    if (r.error) return setConfirmErr(r.error);
    announce(confirm.kind === 'delete' ? t['r5.sq.deleted'] : t['r5.sq.retiredToast']);
    setConfirm(null);
    load();
  }

  const scale = scales?.find((s) => s.code === form.scale);

  if (err && !areas) return <p className="notice notice-block" role="alert">{errorText(t, err)}</p>;
  if (!areas || !scales || !questions) return <p role="status">{t.loading}</p>;

  return (
    <>
      <p className="notice notice-block">{t['r5.sq.hint']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      <section className="panel p5" aria-labelledby={`${id}-new`}>
        <h2 className="panel-title" id={`${id}-new`}>{form.id ? t['r5.sq.editTitle'] : t['r5.sq.newTitle']}</h2>
        <form className="form" onSubmit={save} noValidate>
          <div className="form-row">
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-area`}>{t['r5.sq.area']}</label>
              <div className="select-wrap">
                <select
                  id={`${id}-area`}
                  className="select"
                  value={form.category}
                  disabled={!!form.id}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                >
                  {areas.map((a) => (
                    <option key={a.code} value={a.code}>{areaLabel(t, a.code)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-scale`}>{t['r5.sq.scale']}</label>
              <div className="select-wrap">
                <select
                  id={`${id}-scale`}
                  className="select"
                  value={form.scale}
                  onChange={(e) => setForm((f) => ({ ...f, scale: e.target.value }))}
                >
                  {scales.map((s) => (
                    <option key={s.code} value={s.code}>{scaleLabel(t, s.code)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {scale && (
            <p className="form-hint">
              {t['r5.sq.scalePreview']} {scale.options.map((o) => `${o.emoji} ${o.label}`).join(' · ')}
            </p>
          )}
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-text`}>{t['r5.sq.text']}</label>
            <textarea
              id={`${id}-text`}
              className="field"
              rows={2}
              maxLength={300}
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              aria-describedby={`${id}-text-hint`}
            />
            <p className="form-hint" id={`${id}-text-hint`}>{t['r5.sq.textHint']}</p>
          </div>
          <div className="form-row">
            <fieldset className="field-group">
              <legend className="field-label">{t['r5.sq.direction']}</legend>
              <label className="check">
                <input
                  type="radio"
                  name={`${id}-dir`}
                  checked={form.direction === 'descending'}
                  onChange={() => setForm((f) => ({ ...f, direction: 'descending' }))}
                />
                <span>{t['r5.sq.directionDescending']}</span>
              </label>
              <label className="check">
                <input
                  type="radio"
                  name={`${id}-dir`}
                  checked={form.direction === 'ascending'}
                  onChange={() => setForm((f) => ({ ...f, direction: 'ascending' }))}
                />
                <span>{t['r5.sq.directionAscending']}</span>
              </label>
            </fieldset>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-weight`}>{t['r5.sq.weight']}</label>
              <div className="select-wrap">
                <select
                  id={`${id}-weight`}
                  className="select"
                  value={form.weight}
                  onChange={(e) => setForm((f) => ({ ...f, weight: Number(e.target.value) as 0 | 1 | 2 }))}
                  aria-describedby={`${id}-weight-hint`}
                >
                  {SEVERITY.map((w) => (
                    <option key={w} value={w}>{t[`r5.sq.weight${w}` as 'r5.sq.weight0']}</option>
                  ))}
                </select>
              </div>
              <p className="form-hint" id={`${id}-weight-hint`}>{t['r5.sq.weightHint']}</p>
            </div>
          </div>
          <div className="form-actions">
            {form.id && (
              <button type="button" className="btn btn-ghost" onClick={cancelEdit}>
                {t['r5.sq.cancelEdit']}
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={busy || form.text.trim().length < 1}>
              {form.id ? t['r5.sq.saveEdit'] : t['r5.sq.save']}
            </button>
          </div>
        </form>
      </section>
      <section className="panel p5" aria-labelledby={`${id}-list`}>
        <h2 className="panel-title" id={`${id}-list`}>{t['r5.sq.listTitle']}</h2>
        {questions.length === 0 && <p className="text-muted">{t['r5.sq.empty']}</p>}
        <ul className="approval-list">
          {questions.map((q) => (
            <li key={q.id} className="approval">
              <p>
                <strong>{areaLabel(t, q.category_code)}</strong> · {scaleLabel(t, q.scale_template_code)} · {statusPill(t, q.status)}
              </p>
              <p>{q.question_text}</p>
              <div className="form-actions">
                {q.status !== 'retired' && (
                  <button type="button" className="btn btn-ghost" onClick={() => startEdit(q)}>
                    {t['r5.sq.edit']}
                  </button>
                )}
                {q.status === 'draft' && (
                  <button type="button" className="btn btn-ghost" onClick={() => submit(q)}>
                    {t['r5.sq.submit']}
                  </button>
                )}
                {(q.status === 'draft' || q.status === 'pending') && (
                  <button type="button" className="btn btn-ghost" onClick={() => setConfirm({ kind: 'delete', q })}>
                    {t['r5.sq.delete']}
                  </button>
                )}
                {(q.status === 'approved' || q.status === 'pending') && (
                  <button type="button" className="btn btn-ghost" onClick={() => setConfirm({ kind: 'retire', q })}>
                    {t['r5.sq.retire']}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
      <Dialog
        open={!!confirm}
        title={confirm?.kind === 'delete' ? t['r5.sq.deleteTitle'] : t['r5.sq.retireTitle']}
        intro={<p>{confirm?.kind === 'delete' ? t['r5.sq.deleteBody'] : t['r5.sq.retireBody']}</p>}
        submitLabel={confirm?.kind === 'delete' ? t['r5.sq.delete'] : t['r5.sq.retire']}
        danger={confirm?.kind === 'delete'}
        busy={confirmBusy}
        error={confirmErr ? errorText(t, confirmErr) : null}
        onClose={() => {
          setConfirm(null);
          setConfirmErr(null);
        }}
        onSubmit={runConfirm}
      />
    </>
  );
}

function areaLabel(t: Strings, code: string): string {
  return t[`area.${code}` as 'area.feel'] ?? code;
}
function scaleLabel(t: Strings, code: string): string {
  return t[`scale.${code}` as 'scale.frequency'] ?? code;
}
function statusPill(t: Strings, status: Question['status']) {
  if (status === 'draft') return <Pill kind="warn">{t['ts.draft']}</Pill>;
  if (status === 'pending') return <Pill kind="neutral">{t['r5.sq.statusPending']}</Pill>;
  if (status === 'approved') return <Pill kind="good">{t['r5.sq.statusApproved']}</Pill>;
  return <Pill kind="neutral">{t['r5.sq.statusRetired']}</Pill>;
}
